import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  Stack,
  Chip,
  IconButton,
  Snackbar
} from '@mui/material';
import { ArrowBack, RemoveCircleOutline, Bookmark, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { readSchoolBookmarks, removeSchoolBookmark } from '../utils/schoolBookmarks';
import type { SchoolBookmark } from '../utils/schoolBookmarks';

const typeLabels: Record<SchoolBookmark['type'], string> = {
  material: 'Materials',
  quiz: 'Quizzes',
  assignment: 'Assignments',
  waecTopic: 'WAEC Topics',
  jambTopic: 'JAMB Topics',
};

const SchoolBookmarks: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [bookmarks, setBookmarks] = useState<SchoolBookmark[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    if (!isAuthenticated) {
      setBookmarks([]);
      return () => { mounted = false; };
    }
    readSchoolBookmarks().then((items) => { if (mounted) setBookmarks(items); }).catch(() => {
      if (mounted) {
        setBookmarks([]);
        setSnackbarMessage('Unable to load bookmarks');
        setSnackbarOpen(true);
      }
    });
    return () => { mounted = false; };
  }, [isAuthenticated]);

  const grouped = useMemo(() => {
    const groups: Record<SchoolBookmark['type'], SchoolBookmark[]> = {
      material: [],
      quiz: [],
      assignment: [],
      waecTopic: [],
      jambTopic: [],
    };

    bookmarks.forEach((bookmark) => {
      if (bookmark.type in groups) {
        groups[bookmark.type].push(bookmark);
      }
    });

    return groups;
  }, [bookmarks]);

  const sortedTypes: Array<SchoolBookmark['type']> = ['material', 'quiz', 'assignment', 'waecTopic', 'jambTopic'];

  const handleRemove = async (bookmark: SchoolBookmark) => {
    try {
      await removeSchoolBookmark(bookmark.id, bookmark.type);
      setBookmarks((items) => items.filter((item) => item.id !== bookmark.id || item.type !== bookmark.type));
      setSnackbarMessage('Bookmark removed');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('Unable to remove bookmark');
      setSnackbarOpen(true);
    }
  };

  const handleOpen = (bookmark: SchoolBookmark) => {
    if (bookmark.route) {
      navigate(bookmark.route);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>My School Bookmarks</Typography>
          <Typography color="text.secondary">Saved materials, assignments, quizzes, and topic content from the school section.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Box>

      {bookmarks.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          You have no saved school bookmarks yet. Bookmark school work items or topic content from the school section pages.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {sortedTypes.map((type) => {
            const items = grouped[type];
            if (items.length === 0) return null;

            return (
              <Grid item xs={12} key={type}>
                <Card sx={{ height: '100%', width: '100%' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Bookmark color="primary" />
                        <Typography variant="h6">{typeLabels[type]}</Typography>
                      </Box>
                      <Chip label={items.length} color="primary" variant="outlined" />
                    </Stack>

                    <Grid container spacing={2}>
                      {items.map((bookmark) => (
                        <Grid item xs={12} sm={6} md={4} key={`${bookmark.type}-${bookmark.id}`}>
                          <Card
                            variant="outlined"
                            sx={{
                              p: 1,
                              height: '100%',
                              minWidth: 0,
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                  {bookmark.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {bookmark.subject}
                                </Typography>
                              </Box>
                              <IconButton color="error" onClick={() => handleRemove(bookmark)} aria-label="Remove bookmark">
                                <RemoveCircleOutline />
                              </IconButton>
                            </Box>

                            {bookmark.details && (
                              <Box
                                sx={{
                                  mt: 1,
                                  flex: 1,
                                  minHeight: 110,
                                  maxHeight: 160,
                                  overflowY: 'auto',
                                  pr: 0.5,
                                  '&::-webkit-scrollbar': {
                                    width: '6px'
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0,0,0,0.25)',
                                    borderRadius: '999px'
                                  }
                                }}
                              >
                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                  {bookmark.details}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Saved {new Date(bookmark.savedAt).toLocaleDateString()}
                              </Typography>
                              <Button size="small" variant="contained" startIcon={<OpenInNew />} onClick={() => handleOpen(bookmark)}>
                                Open
                              </Button>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2200}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default SchoolBookmarks;
